// Chess — WebSocket client. Pattern lifted from games/rummy/ws.ts
// so the auth flow, reconnect behaviour, and heartbeat all match
// the rest of the platform byte-for-byte.
//
// Connects to `/api/chess/ws` (the path the server's upgrade handler
// registers in server.js) — NOT `/ws/chess`, which an earlier stub
// assumed and which is why the lobby → board transition previously
// landed on a blank screen (the WS silently 404-reconnect-looped,
// `chess_auth_success` never arrived, `join_queue` was sent against
// a closed socket, so no queue event ever came back).
//
// Auth uses the platform's in-memory access token (+ HTTP-only
// refresh cookie fallback via `api.tryRefresh()`) — the same source
// every other game and every `/api/...` request already uses.

import { getAccessToken, api } from '@baaligames/shared-ui/lib/api';

type Listener  = (msg: any) => void;
export type WSStatus = 'connecting' | 'open' | 'reconnecting' | 'lost';
type StatusFn  = (s: WSStatus) => void;

export class ChessWS {
  private ws: WebSocket | null = null;
  private listeners       = new Set<Listener>();
  private statusListeners = new Set<StatusFn>();
  private closed      = false;
  private reconnecting = false;
  private pingTimer: number | null = null;
  private authFailedOnce = false;
  private boundOnVisibility: (() => void) | null = null;
  private boundOnOnline:     (() => void) | null = null;
  private boundOnOffline:    (() => void) | null = null;

  connect() {
    this.closed = false;
    this.installLifecycleHandlers();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.emitStatus(this.reconnecting ? 'reconnecting' : 'connecting');
    this.ws = new WebSocket(`${proto}://${location.host}/api/chess/ws`);

    this.ws.addEventListener('open', () => {
      const wasReconnecting = this.reconnecting;
      this.emitStatus('open');
      this.auth(wasReconnecting).finally(() => { this.reconnecting = false; });
    });

    this.ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg?.type === 'error' && msg.code === 'auth_failed') this.authFailedOnce = true;
        for (const l of this.listeners) l(msg);
      } catch {}
    });

    this.ws.addEventListener('close', (ev) => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      if (ev && (ev as CloseEvent).code === 4002) this.authFailedOnce = true;
      if (!this.closed) {
        this.reconnecting = true;
        this.emitStatus('lost');
        setTimeout(() => this.connect(), 3000);
      }
    });

    this.ws.addEventListener('error', () => { /* swallow; close handles retry */ });
  }

  private async auth(isReconnect = false) {
    const needsRefresh = !getAccessToken() || isReconnect || this.authFailedOnce;
    if (needsRefresh) {
      await api.tryRefresh();
      this.authFailedOnce = false;
    }
    const token = getAccessToken();
    if (!token || !this.ws) return;
    this.ws.send(JSON.stringify({ type: 'authenticate', token }));
    this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
  }

  onStatus(fn: StatusFn) { this.statusListeners.add(fn); return () => this.statusListeners.delete(fn); }
  private emitStatus(s: WSStatus) { for (const fn of this.statusListeners) fn(s); }

  send(msg: any) {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  on(fn: Listener) { this.listeners.add(fn); return () => this.listeners.delete(fn); }

  close() {
    this.closed = true;
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.removeLifecycleHandlers();
    this.ws?.close();
    this.ws = null;
  }

  // ─── Mobile-friendly lifecycle recovery ────────────────────────────
  //
  // iOS Safari + Android Chrome both aggressively suspend WebSockets
  // when the app is backgrounded. Sometimes the `close` event fires
  // on return-to-foreground (fine — our existing reconnect handles
  // it). Sometimes it doesn't, and the socket is left in a zombie
  // state where `readyState === OPEN` but no frames flow in either
  // direction. That's the "close the whole app to recover" bug you
  // hit on iPhone. Three belt-and-braces handlers:
  //
  //   visibilitychange → visible: if readyState !== OPEN, or we
  //     haven't heard anything in a while, force-close + reconnect.
  //   online:  network just came back → same force-reconnect.
  //   offline: network dropped → flag status 'lost' so the chip
  //     appears immediately instead of waiting for the WS to time
  //     out client-side.
  private installLifecycleHandlers() {
    if (typeof document === 'undefined') return;
    if (this.boundOnVisibility) return;   // already installed
    this.boundOnVisibility = () => {
      if (document.hidden) return;
      if (this.closed) return;
      if (!this.ws || this.ws.readyState !== 1) {
        // Not open (or never was). Nudge reconnect immediately
        // instead of waiting for the 3 s retry timer.
        this.reconnecting = true;
        this.emitStatus('reconnecting');
        try { this.ws?.close(); } catch {}
        setTimeout(() => this.connect(), 50);
      }
    };
    this.boundOnOnline = () => {
      if (this.closed) return;
      if (!this.ws || this.ws.readyState !== 1) {
        this.reconnecting = true;
        this.emitStatus('reconnecting');
        try { this.ws?.close(); } catch {}
        setTimeout(() => this.connect(), 50);
      }
    };
    this.boundOnOffline = () => {
      if (this.closed) return;
      this.emitStatus('lost');
    };
    document.addEventListener('visibilitychange', this.boundOnVisibility);
    window.addEventListener('online',  this.boundOnOnline);
    window.addEventListener('offline', this.boundOnOffline);
  }

  private removeLifecycleHandlers() {
    if (typeof document === 'undefined') return;
    if (this.boundOnVisibility) {
      document.removeEventListener('visibilitychange', this.boundOnVisibility);
      this.boundOnVisibility = null;
    }
    if (this.boundOnOnline) {
      window.removeEventListener('online',  this.boundOnOnline);
      this.boundOnOnline = null;
    }
    if (this.boundOnOffline) {
      window.removeEventListener('offline', this.boundOnOffline);
      this.boundOnOffline = null;
    }
  }
}
